import React, { useState } from "react";
import {
  Header,
  Icon,
  Label,
  Divider,
  Statistic,
  Segment,
  Card,
  Container,
  Popup
} from "semantic-ui-react";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { formatDate, sumArray } from "../../constants";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { radarDataset, radarLabel, radarOptions } from "../../components/chartStyle";
import "../../components/SharedCard.css";
import { CRITERION_ORANGE, evaluatedOutcome, SHOW_CRITERION_EVENT } from "./evaluationRules";
import { jumpTo } from "./reportJump";


ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const SUMMARY_SECTION_TITLES = ['친근하고 신뢰할 수 있는 대화', '고객 니즈 파악', '데모 경험', '제품 추천 및 구매 유도', '신뢰 구축 및 우려 사항 해결', '종합 평가'];

// Jump to a criterion card (with a way back, reportJump.js); on phones the 보완 필요 filter may
// hide it, so its owner is asked to show it.
const showCriterion = (intId, origin) =>
  jumpTo(`criterion_int_id_${intId}`, { showEvent: SHOW_CRITERION_EVENT, detail: intId, origin });

const EvaluationSummary = ({ evaluationsession, hideHeader }) => {
  const { isMobile } = useBreakpoint();
  // Phones show the top 3 of each list and fold 상세 요약 into tap-to-open sections.
  const listLimit = isMobile ? 3 : 5;
  const [expandedLists, setExpandedLists] = useState({});
  const [openSections, setOpenSections] = useState({ 0: true });
  const criterion_set = evaluationsession.evaluatedcriterion_set? 
    evaluationsession.evaluatedcriterion_set : evaluationsession.partnerevaluatedcriterion_set;

  // Helper to find criterion by int_id
  const findCriterion = (int_id) => criterion_set.find(c => c.criterion.int_id === int_id);

  // Process data for Radar Chart (replicating dashboard logic)
  const getTagStats = (criteria) => {
    const stats = {};
    criteria.forEach(c => {
      const tagsString = c.criterion.tags || "기타";
      const tags = tagsString.split(',').map(t => t.trim()).filter(t => t);

      tags.forEach(tag => {
        if (tag === 'deduction') return;

        if (!stats[tag]) {
          stats[tag] = { met: 0, total: 0 };
        }
        stats[tag].total += 1;
        if (evaluatedOutcome(c)?.favorable) {
          stats[tag].met += 1;
        }
      });
    });
    return stats;
  };

  const tagStats = getTagStats(criterion_set);
  const radarLabels = Object.keys(tagStats);
  const radarDataPoints = radarLabels.map(tag => {
      const s = tagStats[tag];
      return (s.met / s.total) * 100;
  });

  const data = {
    labels: radarLabels.map((tag, i) => radarLabel(tag, radarDataPoints[i])),
    datasets: [radarDataset(radarDataPoints)],
  };
  const options = radarOptions();

  // Strengths and Weaknesses
  // An avoided deduction (감점 없음) is neutral: counted as 충족, but not listed as a strength.
  const strengths = criterion_set.filter(c => { const o = evaluatedOutcome(c); return o && o.favorable && !o.deduction; });
  const weaknesses = criterion_set.filter(c => { const o = evaluatedOutcome(c); return o && !o.favorable; });
  // The summary prompt asks for exactly these six, in this order. Gemini (LGU's provider since
  // 2026-10-16) sometimes drops `title` — the schema didn't require it — so a six-section
  // summary falls back to the fixed title for its position.
  const rawSections = evaluationsession.summary?.sections || [];
  const sections = rawSections.map((sec, i) => ({
    ...sec,
    title: sec.title || (rawSections.length === SUMMARY_SECTION_TITLES.length ? SUMMARY_SECTION_TITLES[i] : `요약 ${i + 1}`),
  }));

  return (
    <div>
      
      {/* Top Stats - Modernized */}
      {!hideHeader && (
      <div style={{ 
        display: 'flex', 
        justifyContent: isMobile ? 'flex-start' : 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        flexDirection: isMobile ? 'column' : 'row',
        marginBottom: '2rem',
        padding: '0 8px',
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            width: '40px', 
            flexShrink: 0, // stays a circle when the text beside it wraps
            height: '40px', 
            borderRadius: '50%', 
            backgroundColor: '#F5F5F7', 
            color: '#1d1d1f',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: '600'
          }}>
             {evaluationsession.trainee.full_name ? evaluationsession.trainee.full_name[0] : 'U'}
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f' }}>
              {evaluationsession.trainee.full_name || evaluationsession.trainee.username}
            </div>
            <div style={{ fontSize: '12px', color: '#86868b' }}>
              {formatDate(evaluationsession.created_at)}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            marginTop: isMobile ? '1rem' : 0,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#86868b', marginBottom: '4px' }}>충족 항목</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1d1d1f' }}>
               {criterion_set.filter(c => evaluatedOutcome(c)?.favorable).length} <span style={{ color: '#aaa', fontSize: '13px' }}>/ {criterion_set.length}</span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#86868b', marginBottom: '4px' }}>총점</div>
             <div style={{ fontSize: '16px', fontWeight: '600', color: '#1d1d1f' }}>
                {evaluationsession.total_score} <span style={{ color: '#aaa', fontSize: '13px' }}>/ {sumArray(criterion_set.map((evaluatedcriterion) => evaluatedcriterion.criterion.score))}</span>
             </div>
          </div>
        </div>
      </div>
      )}


      {/* Charts & Lists - Removed Cards */}
      {/* Plain blocks, not Semantic's Grid: its column padding put these 14px further in than 상세 요약. */}
      <div>
        <div>
           <div style={{ marginBottom: '2em' }}>
             <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '16px' }}>영역별 분석</h3>
             <div style={{ height: isMobile ? '240px' : '300px', width: '100%', maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
                {radarLabels.length > 0 ? <Radar data={data} options={options} /> : "데이터 없음"}
             </div>
           </div>
        </div>
        <div>
          <div id="summary_lists" style={{ marginBottom: '2em' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '16px' }}>보완점 & 강점</h3>
            {/* 보완점 first: it's what the trainee acts on next time. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {[
                ['weak', '보완점', '#FF3B30', weaknesses],
                ['strong', '강점', '#34C759', strengths],
              ].map(([key, label, color, items]) => {
                const expanded = !!expandedLists[key];
                return (
                  <div key={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#1d1d1f' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                      {label} <span style={{ color: '#86868b', fontWeight: 400 }}>{items.length}</span>
                    </div>
                    {/* Rows, numbered in 평가 기준 orange like the cards below; tapping one opens
                        that criterion (phones: the filter switches to show it). */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {(expanded ? items : items.slice(0, listLimit)).map((c) => (
                        <button
                          key={c.criterion.int_id}
                          type="button"
                          onClick={(e) => showCriterion(c.criterion.int_id, e.currentTarget)}
                          style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', textAlign: 'left', border: 'none', background: 'none', padding: '4px 0', cursor: 'pointer', fontFamily: 'inherit', fontSize: '13.5px', lineHeight: 1.5, color: '#424245', wordBreak: 'keep-all' }}
                        >
                          <span style={{ color: CRITERION_ORANGE, fontWeight: 700, minWidth: '20px' }}>{c.criterion.int_id + 1}</span>
                          <span>{c.criterion.text}</span>
                        </button>
                      ))}
                    </div>
                    {items.length > listLimit && (
                      <button
                        type="button"
                        onClick={() => setExpandedLists(v => ({ ...v, [key]: !expanded }))}
                        style={{ border: 'none', background: 'none', padding: 0, color: '#007AFF', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {expanded ? '접기' : `${items.length - listLimit}개 더 보기`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '1rem', borderTop: '1px solid #f0f0f0', paddingTop: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '1.5rem' }}>상세 요약</h3>
        
        {sections.length > 0 ? (
          sections.map((section, index) => {
            const open = !isMobile || !!openSections[index];
            // Phones: the same 12px above and below, so a folded title sits centred between
            // the dividers; the title's bottom margin only separates it from an open paragraph.
            return (
            <div key={index} id={`summary_section_${index}`} style={{ marginBottom: isMobile ? 0 : '2em', borderBottom: isMobile ? '1px solid #f0f0f0' : 'none', padding: isMobile ? '12px 0' : 0 }}>
              <h4
                onClick={isMobile ? () => setOpenSections(v => ({ ...v, [index]: !open })) : undefined}
                style={{ fontSize: '14px', fontWeight: '600', color: '#1d1d1f', margin: open ? '0 0 8px' : 0, cursor: isMobile ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{ flex: 1 }}>{section.title}</span>
                {isMobile && <Icon name={open ? 'chevron up' : 'chevron down'} style={{ margin: 0, color: '#c7c7cc', fontSize: '12px' }} />}
              </h4>
              {open && (
              <>
              <p style={{ lineHeight: '1.6', color: '#424245', fontSize: '14px', marginBottom: '12px' }}>
                {section.paragraph}
              </p>
              
              {section.relevant_criteria && section.relevant_criteria.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                   {/* Criteria, not 말풍선: blue is the 말풍선 colour everywhere (bubbles, 관련 말풍선
                       chips), so these use orange — matched by the number on each 응대 스킬 card. */}
                   <span style={{ fontSize: '12px', color: '#86868b', marginRight: '4px' }}>평가 기준:</span>
                   {section.relevant_criteria.map((criterion_int_id) => {
                      const criterion = findCriterion(criterion_int_id);
                      return (
                        <Popup
                          key={criterion_int_id}
                          content={criterion ? criterion.criterion.text : "정보 없음"}
                          size="mini"
                          position="top center"
                          trigger={
                            <span 
                              style={{ 
                                fontSize: '12px', 
                                color: CRITERION_ORANGE, 
                                backgroundColor: '#FFF4E5', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                              onClick={(e) => showCriterion(criterion_int_id, e.currentTarget)}
                            >
                              {criterion_int_id + 1}
                            </span>
                          }
                        />
                      );
                    })}
                </div>
              )}
              </>
              )}
            </div>
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#86868b', fontSize: '13px' }}>
              요약 정보가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationSummary;
